// src/components/receipts/EBillReceipt.jsx
// Redesigned E-Bill: no hardcoded values, full dynamic data, premium design

import { useState, useEffect } from 'react'
import {
  Receipt, MapPin, Phone, Mail, Calendar, Hash,
  User, CreditCard, Package, Award, Star, CheckCircle,
  Clock, ShoppingBag, Tag
} from 'lucide-react'
import { formatUTCDate } from '../../utils/dateUtils'

const EBillReceipt = ({ order }) => {
  const logoSrc = '/logo-web.png';

  if (!order) return null

  // ─────────────── ORDER TYPE ───────────────
  const isDineIn = order.orderType === 'dine_in' || (!!order.table && !!order.table._id)
  const orderTypeLabel =
    order.orderType === 'dine_in'   ? 'Dine In' :
    order.orderType === 'take_away' ? 'Take Away' :
    order.orderType === 'delivery'  ? 'Delivery' :
    order.orderType?.replace(/_/g, ' ') || 'N/A'

  // ─────────────── BRANCH / CONTACT ───────────────
  const branchName    = order.branch?.name        || ''
  const branchAddress = order.branch?.address     || ''
  const branchPhone   = order.branch?.phoneNumber || order.branch?.phone || ''
  const branchEmail   = order.branch?.email       || ''
  const tableInfo     = order.table?.name         || order.table?.number || ''

  // ─────────────── CUSTOMER ───────────────
  const customer      = order.customer || null
  const customerName  = customer?.name || ''
  const customerPhone = customer?.mobileNumber || customer?.phone || ''
  const customerType  = customer?.customerType || ''
  // pointsBalance: show if it's a number (including 0), hide only if missing from API
  const pointsBalance = (customer && typeof customer.pointsBalance === 'number')
    ? customer.pointsBalance
    : null

  // ─────────────── ITEMS ───────────────
  const items = order.orderItems || []

  // ─────────────── TOTALS ───────────────
  const subtotal = order.orderPayment?.subtotal
    || items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0)
    || 0

  const promotionDiscount = order.orderPromotionEngineResult?.orderDetails?.totalDiscount
    || order.orderPayment?.discount
    || 0

  // Use fees object from API (dynamic - no hardcoding)
  const feeObj           = order.fees || {}
  const serviceFeeAmount = feeObj.serviceFeeAmount || 0
  const serviceFeeRate   = feeObj.serviceFeeRate   || 0
  const packageFeeAmount = feeObj.packageFeeAmount || 0
  const packageFeeRate   = feeObj.packageFeeRate   || 0
  const totalFees        = feeObj.totalFees        || serviceFeeAmount + packageFeeAmount

  const apiTotal    = order.orderPayment?.totalAmount
  const finalTotal  = typeof apiTotal === 'number' ? apiTotal : (subtotal - promotionDiscount + totalFees)

  const paidAmount      = order.orderPayment?.paidAmount      || 0
  const remainingAmount = order.orderPayment?.remainingAmount || 0
  const paymentStatus   = order.paymentStatus || ''

  // ─────────────── PAYMENT METHODS ───────────────
  const paymentMethods = order.orderPayment?.paymentMethods || []
  const cashMethod     = paymentMethods.find(m => m.method === 'cash')
  const cardMethods    = paymentMethods.filter(m => m.method === 'card')
  const redeemMethod   = paymentMethods.find(m => m.method === 'redeem')
  const creditMethod   = paymentMethods.find(m => m.method === 'credit')
  const creditAmount   = creditMethod?.amount || 0

  const cashReceived    = cashMethod?.cashDetails?.receivedAmount || cashMethod?.amount || 0
  const cashBillAmt     = cashMethod?.amount || 0
  const changeAmount    = cashMethod?.cashDetails?.changeAmount   || Math.max(0, cashReceived - cashBillAmt)
  const redeemedAmount  = redeemMethod?.amount || 0
  const redeemedPoints  = redeemMethod?.redeemDetails?.pointsUsed || redeemedAmount

  // Helper formatters
  const fmt = (n) => `Rs. ${(n || 0).toFixed(2)}`
  const fmtPts = (n) => `${(n || 0).toFixed(2)} pts`

  return (
    <div className="max-w-2xl mx-auto bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">

      {/* ── HEADER ── */}
      <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white p-8 text-center relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full -translate-y-8 translate-x-8" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-5 rounded-full translate-y-6 -translate-x-6" />

        <div className="relative z-10">
          {logoSrc ? (
            <div className="flex justify-center mb-4">
              <img
                src={logoSrc}
                alt="Brand Logo"
                className="h-20 w-auto object-contain bg-white rounded-lg p-1 shadow-lg"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center mb-4">
              <Receipt className="h-12 w-12 mr-3 opacity-90" />
            </div>
          )}
          <h1 className="text-3xl font-bold tracking-widest uppercase">
            {branchName || 'TEZLAA CAFÉ'}
          </h1>
          <p className="text-orange-100 text-sm mt-2 font-medium tracking-wider uppercase">
            Digital Receipt · E-Bill
          </p>
        </div>
      </div>

      {/* ── BRANCH INFO ── */}
      {(branchAddress || branchPhone || branchEmail) && (
        <div className="bg-orange-50 px-6 py-4 border-b border-orange-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {branchAddress && (
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700 text-xs uppercase tracking-wide mb-0.5">Location</p>
                  <p className="text-gray-600">{branchAddress}</p>
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              {branchPhone && (
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-600">{branchPhone}</span>
                </div>
              )}
              {branchEmail && (
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-600">{branchEmail}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── ORDER DETAILS GRID ── */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-1.5 text-gray-400 mb-1">
              <Hash className="h-3.5 w-3.5" />
              <span className="text-xs font-medium uppercase tracking-wide">Order #</span>
            </div>
            <p className="text-gray-900 font-bold text-base">{order.orderNumber || 'N/A'}</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-1.5 text-gray-400 mb-1">
              <Calendar className="h-3.5 w-3.5" />
              <span className="text-xs font-medium uppercase tracking-wide">Date</span>
            </div>
            <p className="text-gray-900 text-xs leading-snug">
              {formatUTCDate(order.orderDate || order.createdAt, 'dd/MM/yyyy')}
              <br />
              {formatUTCDate(order.orderDate || order.createdAt, 'HH:mm')}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-1.5 text-gray-400 mb-1">
              <ShoppingBag className="h-3.5 w-3.5" />
              <span className="text-xs font-medium uppercase tracking-wide">Type</span>
            </div>
            <p className="text-gray-900 font-semibold capitalize">{orderTypeLabel}</p>
            {tableInfo && isDineIn && (
              <p className="text-xs text-orange-600 mt-0.5">Table: {tableInfo}</p>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-1.5 text-gray-400 mb-1">
              <CreditCard className="h-3.5 w-3.5" />
              <span className="text-xs font-medium uppercase tracking-wide">Status</span>
            </div>
            <div className="flex items-center space-x-1">
              {paymentStatus === 'paid' ? (
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              ) : paymentStatus === 'pending' ? (
                <Clock className="h-4 w-4 text-yellow-500 flex-shrink-0" />
              ) : null}
              <p className="text-gray-900 font-semibold capitalize text-sm">
                {paymentStatus || 'N/A'}
              </p>
            </div>
            {paymentMethods.length > 0 && (
              <p className="text-xs text-gray-500 mt-0.5 capitalize">
                {paymentMethods.map(m => m.method).join(', ')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── CUSTOMER INFO ── */}
      {customerName && (
        <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center space-x-2 mb-3">
            <User className="h-5 w-5 text-blue-600" />
            <h3 className="font-bold text-gray-800">Customer Information</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-7 text-sm">
            <div>
              <span className="text-gray-500 text-xs uppercase tracking-wide">Name</span>
              <p className="text-gray-900 font-semibold">{customerName}</p>
            </div>
            {customerPhone && (
              <div>
                <span className="text-gray-500 text-xs uppercase tracking-wide">Phone</span>
                <p className="text-gray-900">{customerPhone}</p>
              </div>
            )}
            {customerType && (
              <div>
                <span className="text-gray-500 text-xs uppercase tracking-wide">Member Type</span>
                <p className="text-gray-900 uppercase font-medium">{customerType}</p>
              </div>
            )}
            {pointsBalance !== null && (
              <div>
                <span className="text-gray-500 text-xs uppercase tracking-wide">Loyalty Balance</span>
                <p className="text-blue-700 font-bold">{fmtPts(pointsBalance)}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ITEMS TABLE ── */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-2 mb-4">
          <Package className="h-5 w-5 text-orange-500" />
          <h3 className="font-bold text-gray-800 text-lg">Order Items</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200 text-gray-500">
                <th className="text-left py-2 px-2 font-semibold text-xs uppercase tracking-wide">Item</th>
                <th className="text-center py-2 px-2 font-semibold text-xs uppercase tracking-wide w-12">Qty</th>
                <th className="text-right py-2 px-2 font-semibold text-xs uppercase tracking-wide w-24">Unit Price</th>
                <th className="text-right py-2 px-2 font-semibold text-xs uppercase tracking-wide w-24">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-2">
                    <p className="font-medium text-gray-900">
                      {item.product?.name || item.name || `Item #${index + 1}`}
                    </p>
                    {item.product?.sku && (
                      <p className="text-xs text-gray-400">SKU: {item.product.sku}</p>
                    )}
                    {item.promotionEngineResult?.hasPromotion && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Tag className="h-3 w-3 text-green-600" />
                        <p className="text-xs text-green-600 font-medium">
                          {item.promotionEngineResult.promotion?.name || 'Promotion Applied'}
                        </p>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center text-gray-900 font-semibold">{item.quantity}</td>
                  <td className="py-3 px-2 text-right text-gray-600">
                    Rs. {(item.unitPrice || 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-900 font-semibold">
                    Rs. {((item.unitPrice || 0) * (item.quantity || 1)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── TOTALS ── */}
        <div className="mt-6 space-y-1.5">
          <div className="flex justify-between text-sm py-2 border-t border-gray-200">
            <span className="text-gray-500">Subtotal</span>
            <span className="text-gray-900 font-medium">{fmt(subtotal)}</span>
          </div>

          {promotionDiscount > 0 && (
            <div className="flex justify-between text-sm py-1.5 px-2 bg-green-50 rounded">
              <span className="text-green-700 flex items-center space-x-1">
                <Tag className="h-3.5 w-3.5" />
                <span>Promotion Discount</span>
              </span>
              <span className="text-green-700 font-semibold">- {fmt(promotionDiscount)}</span>
            </div>
          )}

          {/* Service fee — only from API fees object, no hardcoded 10% */}
          {serviceFeeAmount > 0 && (
            <div className="flex justify-between text-sm py-1.5">
              <span className="text-gray-500">
                Service Charge{serviceFeeRate > 0 ? ` (${serviceFeeRate}%)` : ''}
              </span>
              <span className="text-gray-900 font-medium">{fmt(serviceFeeAmount)}</span>
            </div>
          )}

          {packageFeeAmount > 0 && (
            <div className="flex justify-between text-sm py-1.5">
              <span className="text-gray-500">
                Package Fee{packageFeeRate > 0 ? ` (${packageFeeRate}%)` : ''}
              </span>
              <span className="text-gray-900 font-medium">{fmt(packageFeeAmount)}</span>
            </div>
          )}

          {/* Redeemed points used in payment */}
          {redeemedPoints > 0 && (
            <div className="flex justify-between text-sm py-1.5 px-2 bg-purple-50 rounded">
              <span className="text-purple-700 flex items-center space-x-1">
                <Award className="h-3.5 w-3.5" />
                <span>Points Redeemed ({fmtPts(redeemedPoints)})</span>
              </span>
              <span className="text-purple-700 font-semibold">- {fmt(redeemedAmount)}</span>
            </div>
          )}

          <div className="flex justify-between text-xl font-bold py-3 border-t-2 border-gray-800 mt-2">
            <span className="text-gray-900">Total Amount</span>
            <span className="text-orange-600">{fmt(finalTotal)}</span>
          </div>
        </div>
      </div>

      {/* ── PAYMENT DETAILS ── */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-2 mb-3">
          <CreditCard className="h-5 w-5 text-gray-600" />
          <h3 className="font-bold text-gray-800">Payment Details</h3>
        </div>

        <div className="space-y-2 text-sm">
          {cashReceived > 0 && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Cash Received</span>
                <span className="text-gray-900 font-medium">{fmt(cashReceived)}</span>
              </div>
              {changeAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Change Returned</span>
                  <span className="text-gray-900 font-medium">{fmt(changeAmount)}</span>
                </div>
              )}
            </>
          )}

          {cardMethods.map((c, i) => (
            <div key={i} className="flex justify-between">
              <span className="text-gray-600">
                Card{c.cardDetails?.lastFourDigits ? ` (****${c.cardDetails.lastFourDigits})` : ''}
              </span>
              <span className="text-gray-900 font-medium">{fmt(c.amount)}</span>
            </div>
          ))}

          {redeemedAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-purple-700">Points Redeemed</span>
              <span className="text-purple-700 font-medium">{fmt(redeemedAmount)}</span>
            </div>
          )}

          {creditAmount > 0 && (
            <div className="flex justify-between text-purple-700 font-semibold bg-purple-50 p-2 rounded-lg">
              <span>Charged to Customer Credit</span>
              <span>{fmt(creditAmount)}</span>
            </div>
          )}

          {paidAmount > 0 && (
            <div className="flex justify-between font-semibold border-t border-gray-200 pt-2 mt-2">
              <span className="text-gray-700">Total Paid</span>
              <span className="text-gray-900">{fmt(paidAmount)}</span>
            </div>
          )}

          {remainingAmount > 0 && (
            <div className="flex justify-between px-3 py-2 bg-amber-50 rounded-lg">
              <span className="text-amber-700 font-semibold">Balance Due</span>
              <span className="text-amber-700 font-bold">{fmt(remainingAmount)}</span>
            </div>
          )}

          {paymentStatus === 'paid' && remainingAmount === 0 && (
            <div className="flex items-center justify-center space-x-2 px-3 py-2 bg-green-50 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-700 font-semibold">Payment Complete</span>
            </div>
          )}
        </div>
      </div>

      {/* ── LOYALTY POINTS SECTION ── */}
      {/* Show if there was a redemption OR if we have a logged-in customer with points data */}
      {(redeemedPoints > 0 || pointsBalance !== null) && (
        <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
          <div className="flex items-center space-x-2 mb-3">
            <Star className="h-5 w-5 text-purple-600" />
            <h3 className="font-bold text-purple-900">Loyalty Points</h3>
          </div>
          <div className="ml-7 space-y-1.5 text-sm">
            {redeemedPoints > 0 && (
              <div className="flex justify-between">
                <span className="text-purple-700">Points Redeemed</span>
                <span className="text-purple-700 font-bold">{fmtPts(redeemedPoints)}</span>
              </div>
            )}
            {pointsBalance !== null && (
              <div className="flex justify-between">
                <span className="text-purple-700">Current Points Balance</span>
                <span className="text-purple-900 font-bold">{fmtPts(pointsBalance)}</span>
              </div>
            )}
            {redeemedPoints <= 0 && pointsBalance !== null && (
              <p className="text-purple-500 text-xs mt-1">Earn points on every purchase!</p>
            )}
          </div>
        </div>
      )}

      {/* ── NOTES ── */}
      {order.notes && (
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
            <span className="font-semibold text-yellow-800">Note: </span>
            <span className="text-yellow-900">{order.notes}</span>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <div className="bg-gradient-to-b from-gray-50 to-white p-6 text-center">
        <p className="text-gray-800 font-bold text-lg mb-1">
          Thank you for choosing {branchName || 'TEZLAA CAFÉ'}!
        </p>
        <p className="text-sm text-gray-500 mb-4">We hope to see you again soon. 😊</p>

        <div className="border-t border-dashed border-gray-300 pt-4">
          <p className="text-xs text-gray-400">
            Computer-generated receipt — no signature required.
          </p>
          <p className="text-xs text-gray-400 mt-1">Receipt ID: {order._id || 'N/A'}</p>
        </div>
      </div>

      {/* ── ACTION BUTTONS (no-print) ── */}
      <div className="bg-white px-6 py-4 flex flex-col sm:flex-row justify-center items-center gap-3 border-t border-gray-200 no-print">
        <button
          onClick={() => window.print()}
          className="w-full sm:w-auto px-8 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 active:scale-95 transition-all font-semibold shadow-md"
        >
          🖨️ Print Receipt
        </button>
        <button
          onClick={() => window.print()}
          title="Select 'Save as PDF' in the print dialog"
          className="w-full sm:w-auto px-8 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 active:scale-95 transition-all font-semibold shadow-md"
        >
          📄 Save as PDF
        </button>
      </div>
    </div>
  )
}

export default EBillReceipt
